import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/constants/routes';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { Banner, Button, Flex, Text } from '@electricjs/arc';
import { useGetMDMInformationQuery } from '@/redux/slices/mdmManagementApiSlice';
import { differenceInDays } from 'date-fns';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { skipToken } from '@reduxjs/toolkit/query';

const MDMCertificateExpirationBanner = () => {
  const { t } = useTranslation('mdm');
  const navigate = useNavigate();

  const organizationId = useGetOrganizationId();

  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const { data } = useGetMDMInformationQuery(
    hasMdmEntitlement ? { organizationId } : skipToken
  );

  if (!hasMdmEntitlement) return null;

  const expirationDate =
    data?.appleCertificate?.expiresAt !== undefined
      ? new Date(data.appleCertificate.expiresAt)
      : null;

  if (!expirationDate) return null;

  const currentDate = new Date();
  const daysDifference = differenceInDays(expirationDate, currentDate);

  const isExpirationAboutToExpire = daysDifference <= 30 && daysDifference > 0;
  const isExpired = daysDifference <= 0;

  const AboutToExpireBanner = (
    <Flex mb="2rem">
      <Banner
        id="mdm-certificate-expiration-banner"
        isFlat
        isFullWidth
        title={t('certificateExpiration.bannerTitle')}
        intent="warning"
        message={() => (
          <Text>
            {t('certificateExpiration.willExpireMessage', {
              date: expirationDate.toLocaleDateString(),
            })}
            <br />
            {t('certificateExpiration.renewInstructions', {
              appleId: data?.appleCertificate?.creatorAppleId,
            })}
          </Text>
        )}
        cta={
          <Flex mt="1rem">
            <Button
              id="mdm-renew-now-button"
              iconAfter="arrow-right"
              onClick={() =>
                navigate(ROUTES.SETTINGS_DEVICE, {
                  state: { renewCertificate: true },
                })
              }
              intent="warning"
              variant="outline">
              {t('certificateExpiration.renewButton')}
            </Button>
          </Flex>
        }
      />
    </Flex>
  );

  const ExpiredBanner = (
    <Flex mb="2rem">
      <Banner
        id="mdm-certificate-expiration-banner"
        isFlat
        isFullWidth
        title={t('certificateExpiration.bannerTitle')}
        intent="error"
        message={() => (
          <Text>
            {t('certificateExpiration.expiredMessage', {
              date: expirationDate.toLocaleDateString(),
            })}
            <br />
            {t('certificateExpiration.renewInstructions', {
              appleId: data?.appleCertificate?.creatorAppleId,
            })}
          </Text>
        )}
        cta={
          <Button
            id="mdm-renew-now-button"
            iconAfter="arrow-right"
            onClick={() =>
              navigate(ROUTES.SETTINGS_DEVICE, {
                state: { renewCertificate: true },
              })
            }
            intent="danger"
            variant="outline">
            {t('certificateExpiration.renewButton')}
          </Button>
        }
        ctaSecondary={t('certificateExpiration.contactSupport')}
        onClickCtaSecondary={() =>
          (window.location.href = 'mailto:product-support@electric.ai')
        }
      />
    </Flex>
  );

  if (isExpired) {
    return ExpiredBanner;
  }

  if (isExpirationAboutToExpire) {
    return AboutToExpireBanner;
  }

  return null;
};

export default MDMCertificateExpirationBanner;
